// @flow

import React, { Component, Fragment, type Node } from "react";
import { noop, get, isEqual } from "lodash";
import type { NullaryFn, UnaryFn } from "@tvg/types/Functional";
import type { RaceCardRunner } from "@tvg/types/Race";
import type { PastPerformanceRace } from "@fdr/types/PastPerformance";
import buildColor from "@fdr/static-ui/ColorPalette";
import Icon from "@fdr/static-ui/Icons";
import { info } from "@fdr/static-ui/Icons/icons";
import FeedbackOverlay from "../../_atoms/FeedbackOverlay";
import InlinePastPerformanceInfo from "../../_molecules/PastPerformanceRace";
import Video from "../../_organisms/Video";

import {
  InfoContainer,
  LoadingState,
  ContentWrapper,
  ModalWrapper,
  VideoContainer,
  NoReplayContainer,
  NoReplayContainerMessage,
  IconContainer,
  MessageContainer
} from "./styled-components";

type Props = {
  race: PastPerformanceRace,
  runner: ?RaceCardRunner,
  hasNextRace: boolean,
  hasPreviousRace: boolean,
  onPreviousRaceNavigation: NullaryFn<void>,
  onNextRaceNavigation: NullaryFn<void>,
  withBorders: boolean,
  isLoading: boolean,
  replaySrc: ?string,
  sendVideoGtmEvent: NullaryFn<void>,
  videoFullscreenTilt: boolean,
  deviceLockRotationToggle: UnaryFn<boolean, void>,
  renderTabs: Node,
  renderComponent: Node
};

export default class HorsePastPerformances extends Component<Props> {
  static defaultProps = {
    runner: null,
    hasNextRace: false,
    hasPreviousRace: false,
    onPreviousRaceNavigation: noop,
    onNextRaceNavigation: noop,
    withBorders: false,
    videoFullscreenTilt: false,
    isLoading: true,
    replaySrc: null,
    sendVideoGtmEvent: noop,
    deviceLockRotationToggle: noop,
    renderTabs: noop,
    renderComponent: noop
  };

  shouldComponentUpdate(nextProps: Props) {
    const { race, runner } = this.props;

    return (
      !isEqual(race, nextProps.race) ||
      !isEqual(runner, nextProps.runner) ||
      !isEqual(this.props, nextProps)
    );
  }

  renderVideo = () => (
    <Video
      raceId={get(this.props, "race.id")}
      src={{ hd: null, sd: null, replay: this.props.replaySrc }}
      isReplay
      inlineVideo
      onVideoPlay={this.props.sendVideoGtmEvent}
      videoFullscreenTilt={this.props.videoFullscreenTilt}
      deviceLockRotationToggle={this.props.deviceLockRotationToggle}
      isAutoPlay={false}
    />
  );

  renderVideoFallback = () => (
    <Fragment>
      <NoReplayContainer />
      <NoReplayContainerMessage>
        <IconContainer>
          <Icon icon={info} color={buildColor("white", "100")} size={16} />
        </IconContainer>
        <MessageContainer>No replay available</MessageContainer>
      </NoReplayContainerMessage>
    </Fragment>
  );

  render() {
    return (
      <ModalWrapper>
        <ContentWrapper withBorders={this.props.withBorders}>
          {this.props.isLoading ? (
            <LoadingState>
              <FeedbackOverlay
                loadingIconColor={buildColor("grey", "900")}
                overlayBgColor={buildColor("white", "100")}
              />
            </LoadingState>
          ) : (
            <Fragment>
              <InfoContainer borderless={this.props.withBorders}>
                <InlinePastPerformanceInfo
                  race={this.props.race}
                  hasNextRace={this.props.hasNextRace}
                  hasPreviousRace={this.props.hasPreviousRace}
                  useSimpleLayout
                  onPreviousRaceNavigation={this.props.onPreviousRaceNavigation}
                  onNextRaceNavigation={this.props.onNextRaceNavigation}
                />
              </InfoContainer>
              <VideoContainer
                isVideoAvailable={
                  get(this.props, "race.hasReplays") &&
                  get(this.props, "replaySrc")
                }
                isPlaying={!!get(this.props, "replaySrc")}
              >
                {get(this.props, "race.hasReplays")
                  ? this.renderVideo()
                  : this.renderVideoFallback()}
              </VideoContainer>
              <Fragment>
                {this.props.renderTabs}
                {this.props.renderComponent}
              </Fragment>
            </Fragment>
          )}
        </ContentWrapper>
      </ModalWrapper>
    );
  }
}
